import { useState, useEffect } from "react";
import apiClient from "../services/api";
import { toast } from "sonner";
import { 
  User, 
  Bell, 
  MessageSquare, 
  Mail, 
  Save, 
  Phone,
  AtSign,
  Shield,
  Lock,
  Eye,
  EyeOff,
  Smartphone,
  BellRing
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Separator } from "../components/ui/separator";
import PasskeyDevicesCard from "../components/profile/PasskeyDevicesCard";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { formatPhoneNumber } from "../lib/formatters";
const NOTIFICATION_TYPES = [
  { key: "missing_reports", label: "Missing Daily Reports", description: "Alerts when jobs are missing daily reports", roles: "all" },
  { key: "backup_reminder", label: "Backup Reminder", description: "Reminder when database hasn't been backed up", roles: "managers" },
  { key: "bid_due", label: "Bid Due Dates", description: "Alerts for upcoming bid deadlines", roles: "managers" },
  { key: "billing_due", label: "Billing Due Dates", description: "Alerts for billing payment deadlines", roles: "managers" },
  { key: "invoice_due", label: "Invoice Due Dates", description: "Alerts for invoice payment deadlines", roles: "managers" },
  { key: "note_due", label: "Follow-Up Reminders", description: "Alerts for notes and follow-ups that are due", roles: "managers" },
  { key: "training_expiration", label: "Training Certificate Expiration", description: "Alerts when employee certificates are expiring or expired", roles: "managers" },
  { key: "tasks", label: "Tasks / To-Dos", description: "When a task is assigned to you or is due", roles: "all" },
  { key: "photo_upload", label: "Photo Uploads", description: "When a foreman uploads job photos", roles: "managers" },
  { key: "new_application", label: "Employment Applications", description: "When someone submits an employment application", roles: "managers" },
  { key: "certified_payroll_due", label: "Certified Payroll Due", description: "Monday reminder when a Davis-Bacon job has hours last week but no WH-347 filed", roles: "managers" },
  { key: "mail_health", label: "Notification Mailbox Health", description: "When the Microsoft 365 mailbox disconnects or an email fails to send", roles: "admin", noEmail: true },
];

const DIGEST_SECTIONS = [
  { key: "hours", label: "Hours logged today", description: "Hours per job with the foreman who submitted" },
  { key: "missing", label: "Missing daily reports", description: "Jobs that should have an entry but don't" },
  { key: "receipts", label: "Receipts awaiting review", description: "Count of pending receipts" },
  { key: "applicants", label: "New applicants", description: "Employment applications received today" },
  { key: "address_gaps", label: "Jobs missing an address", description: "Active jobs with no street address (location matching won't work)" },
  { key: "birthdays", label: "Upcoming birthdays", description: "Active employees with a birthday in the next 7 days" },
  { key: "weekly", label: "Weekly summary on Fridays", description: "Adds a Monday–Friday hours and missing-report recap to Friday's email" },
];

const ProfilePage = () => {
  const { getAuthHeaders, user } = useAuth();
  const {
    pushSupported,
    pushSubscribed,
    isStandalone,
    subscribeToPush,
    unsubscribeFromPush,
  } = useNotifications();
  const [pushBusy, setPushBusy] = useState(false);
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    role: "",
  });
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Notification preferences state
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [appEnabled, setAppEnabled] = useState(true);
  const [notificationTypes, setNotificationTypes] = useState({});
  const [digestPrefs, setDigestPrefs] = useState({ hours: true, missing: true, receipts: true, applicants: true, address_gaps: true, birthdays: true, weekly: true });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, prefsRes] = await Promise.all([
          apiClient.get(`/users/me/profile`),
          apiClient.get(`/users/me/notification-preferences`),
        ]);
        
        setProfile({
          name: profileRes.data.name || "",
          username: profileRes.data.username || "",
          email: profileRes.data.email || "",
          phone: profileRes.data.phone || "",
          role: profileRes.data.role || "",
        });
        
        setEmailEnabled(prefsRes.data.email_enabled);
        setAppEnabled(prefsRes.data.app_enabled);
        setNotificationTypes(prefsRes.data.notification_types || {});
        if (prefsRes.data.digest) setDigestPrefs(prefsRes.data.digest);
      } catch (error) {
        toast.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [getAuthHeaders]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await apiClient.put(
        `/users/me/profile`,
        {
          email: profile.email,
          phone: profile.phone,
        }
      );
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      const res = await subscribeToPush();
      if (res.ok) {
        toast.success("Push notifications enabled on this device");
      } else if (res.reason === "denied") {
        toast.error("Notifications were blocked. Enable them in your device settings.");
      } else if (res.reason === "unsupported") {
        toast.error("This browser doesn't support push notifications.");
      } else if (res.reason === "not_configured") {
        toast.error("Push notifications aren't configured on the server yet.");
      } else {
        toast.error("Could not enable push notifications.");
      }
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      toast.success("Push notifications disabled on this device");
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    setPushBusy(true);
    try {
      let endpoint = null;
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        endpoint = sub?.endpoint || null;
      }
      const { data } = await apiClient.post(`/push/test`, { endpoint });
      if (data.sent > 0) {
        toast.success("Test notification sent to this device");
      } else if (data.total === 0) {
        toast.error("This device isn't registered on the server. Turn push off, then Enable again.");
      } else {
        const r = data.results?.[0];
        toast.error(`Push was rejected (status ${r?.status ?? "?"}). Turn push off, then Enable again.`);
      }
    } catch (error) {
      toast.error("Failed to send test notification");
    } finally {
      setPushBusy(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await apiClient.put(
        `/users/me/notification-preferences`,
        {
          email_enabled: emailEnabled,
          app_enabled: appEnabled,
          notification_types: notificationTypes,
          digest: digestPrefs,
        }
      );
      toast.success("Notification preferences saved");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setSavingPassword(true);
    try {
      await apiClient.post(
        `/auth/change-password`,
        {
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword,
        }
      );
      toast.success("Password changed successfully");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setSavingPassword(false);
    }
  };

  const updateNotificationType = (typeKey, channel, value) => {
    setNotificationTypes(prev => ({
      ...prev,
      [typeKey]: {
        ...prev[typeKey],
        [channel]: value,
      },
    }));
  };

  const getTypePreference = (typeKey, channel) => {
    return notificationTypes[typeKey]?.[channel] ?? true;
  };

  // Determine which notification types this user role can configure
  const availableTypes = NOTIFICATION_TYPES.filter(type => {
    if (user?.role === "foreman" || user?.role === "superintendent") {
      return type.key === "missing_reports" || type.key === "tasks";
    }
    if (type.roles === "admin") return user?.role === "admin";
    return true;
  });

  const hasEmail = Boolean(profile.email);

  const formatRoleName = (role) => {
    return role?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="page-enter space-y-8 max-w-4xl" data-testid="profile-page">
      <div className="page-header">
        <h1 className="text-4xl font-bold tracking-tight text-foreground">My Profile</h1>
      </div>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Information
          </CardTitle>
          <CardDescription>Update your contact details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Read-only info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Name</Label>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{profile.name}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Role</Label>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{formatRoleName(profile.role)}</span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Editable contact info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="your@email.com"
                  className="pl-10"
                  data-testid="profile-email"
                />
              </div>
              <p className="text-xs text-muted-foreground">Used for email notifications</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile(prev => ({ ...prev, phone: formatPhoneNumber(e.target.value) }))}
                  placeholder="(555) 123-4567"
                  className="pl-10"
                  data-testid="profile-phone"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              onClick={handleSaveProfile} 
              disabled={savingProfile}
              data-testid="save-profile-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingProfile ? "Saving..." : "Save Contact Info"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Passkeys on this account */}
      <PasskeyDevicesCard />

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="Enter current password"
                  data-testid="current-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="Enter new password"
                  data-testid="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type={showNewPassword ? "text" : "password"}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Confirm new password"
                data-testid="confirm-password"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Password must be at least 8 characters long
          </p>
          <div className="flex justify-end">
            <Button 
              onClick={handleChangePassword} 
              disabled={savingPassword}
              variant="outline"
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              {savingPassword ? "Changing..." : "Change Password"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notification Channels
          </CardTitle>
          <CardDescription>Choose how you want to receive notifications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-blue-600" />
              <div>
                <Label className="text-base">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  {hasEmail ? "Receive email alerts" : "Add email address above to enable"}
                </p>
              </div>
            </div>
            <Switch
              checked={emailEnabled}
              onCheckedChange={setEmailEnabled}
              disabled={!hasEmail}
              data-testid="email-toggle"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              <div>
                <Label className="text-base">In-App Notifications</Label>
                <p className="text-sm text-muted-foreground">See alerts within the app</p>
              </div>
            </div>
            <Switch
              checked={appEnabled}
              onCheckedChange={setAppEnabled}
              data-testid="app-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications (this device) */}
      <Card data-testid="push-notifications-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellRing className="w-5 h-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Get alerts on this device — even when the app is closed. Set this up on each device you use.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isIOS && !isStandalone ? (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <Smartphone className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Add V2R Masonry to your Home Screen first</p>
                <p className="mt-1">
                  On iPhone/iPad, tap the <span className="font-medium">Share</span> button in Safari, choose
                  <span className="font-medium"> "Add to Home Screen,"</span> then open the app from the new icon.
                  You can turn on push notifications from there.
                </p>
              </div>
            </div>
          ) : !pushSupported ? (
            <p className="text-sm text-muted-foreground" data-testid="push-unsupported">
              Push notifications aren't supported on this browser.
            </p>
          ) : pushSubscribed ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700" data-testid="push-enabled-status">
                <BellRing className="w-4 h-4" />
                <span className="font-medium">Push notifications are ON for this device</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={handleTestPush}
                  disabled={pushBusy}
                  data-testid="push-test-btn"
                >
                  Send test notification
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleDisablePush}
                  disabled={pushBusy}
                  data-testid="push-disable-btn"
                >
                  {pushBusy ? "Turning off..." : "Turn off on this device"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Turn on push to get job alerts (missing daily reports, follow-ups, due dates) pushed straight to this device.
              </p>
              <Button
                onClick={handleEnablePush}
                disabled={pushBusy}
                data-testid="push-enable-btn"
              >
                <BellRing className="w-4 h-4 mr-2" />
                {pushBusy ? "Enabling..." : "Enable push on this device"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-Notification Type Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Types</CardTitle>
          <CardDescription>Fine-tune which notifications you receive and how</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {availableTypes.map((type) => (
              <div key={type.key} className="border rounded-lg p-4">
                <div className="mb-3">
                  <h4 className="font-medium">{type.label}</h4>
                  <p className="text-sm text-muted-foreground">{type.description}</p>
                </div>
                <div className="flex flex-wrap gap-4">
                  {type.key !== "tasks" && !type.noEmail && (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={getTypePreference(type.key, "email")}
                      onCheckedChange={(v) => updateNotificationType(type.key, "email", v)}
                      disabled={!hasEmail || !emailEnabled}
                      data-testid={`${type.key}-email`}
                    />
                    <Label className="text-sm">Email</Label>
                  </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={getTypePreference(type.key, "app")}
                      onCheckedChange={(v) => updateNotificationType(type.key, "app", v)}
                      disabled={!appEnabled}
                      data-testid={`${type.key}-app`}
                    />
                    <Label className="text-sm">In-App</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={getTypePreference(type.key, "push")}
                      onCheckedChange={(v) => updateNotificationType(type.key, "push", v)}
                      data-testid={`${type.key}-push`}
                    />
                    <Label className="text-sm">Push</Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* End-of-day digest (office managers / admins) */}
      {(user?.role === "office_manager" || user?.role === "admin") && (
        <Card data-testid="digest-prefs-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              End-of-Day Summary Email
            </CardTitle>
            <CardDescription>Sent 4:15 PM CT on workdays to office managers. Choose which sections to include.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {DIGEST_SECTIONS.map((sec) => (
              <div key={sec.key} className="flex items-center justify-between">
                <div>
                  <Label className="text-sm">{sec.label}</Label>
                  <p className="text-xs text-muted-foreground">{sec.description}</p>
                </div>
                <Switch
                  checked={digestPrefs[sec.key] ?? true}
                  onCheckedChange={(v) => setDigestPrefs((prev) => ({ ...prev, [sec.key]: v }))}
                  data-testid={`digest-${sec.key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Save Notification Preferences Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSaveNotifications} 
          disabled={savingNotifications}
          data-testid="save-notification-prefs"
        >
          <Save className="w-4 h-4 mr-2" />
          {savingNotifications ? "Saving..." : "Save Notification Preferences"}
        </Button>
      </div>
    </div>
  );
};

export default ProfilePage;
